import { useQuery } from "@tanstack/react-query";
import { createQuery } from "@/lib/queryClient";

// Types for the availability API response
interface AvailabilityResponse {
  available: boolean;
  availability: {
    local_bookings: any[];
    external_reservations: any[];
    blackout_dates: any[];
    holds: any[];
    available_dates: string[];
    // Legacy response metadata only. Never use another home's arrival to disable dates.
    arrival_unavailable_dates?: string[];
    blocked_dates: Array<{ date: string; reason: string; source: 'booking' | 'external' | 'blackout' | 'hold' | 'buffer' | 'short_gap' | string }>;
  };
}

// Hook options interface
interface UseUnavailableDatesOptions {
  enabled?: boolean;
  refetchInterval?: number;
  propertySlug?: string;
}

/**
 * Custom hook to fetch unavailable dates for a date range
 * Used by date picker components to disable unavailable dates
 */
export function useUnavailableDates(
  startDate: string,
  endDate: string,
  options: UseUnavailableDatesOptions = {}
) {
  const { enabled = true, refetchInterval, propertySlug } = options;

  // Only fetch if we have valid start and end dates
  const shouldFetch = enabled && startDate && endDate && startDate <= endDate;

  const query = useQuery({
    ...createQuery(
      ["/api/availability/merged", { start: startDate, end: endDate, ...(propertySlug ? { property: propertySlug } : {}) }],
      {
        // Slightly more frequent refresh for availability data
        staleTime: 1000 * 60 * 3, // 3 minutes (shorter than default for dynamic data)
        gcTime: 1000 * 60 * 10, // 10 minutes
        refetchOnMount: 'always' as const,
        refetchOnWindowFocus: true,
        refetchInterval: refetchInterval,
        // More resilient network handling
        networkMode: 'online' as const, // Allow offline usage with cached data
        // Enhanced retry logic for better resilience
        retry: (failureCount: number, error: Error) => {
          // Don't retry on client errors (4xx) except timeouts
          if (error instanceof Error && error.message.includes('4')) {
            const statusMatch = error.message.match(/^(\d+):/);
            if (statusMatch) {
              const statusCode = parseInt(statusMatch[1]);
              if (statusCode >= 400 && statusCode < 500 && statusCode !== 408) {
                return false; // Don't retry client errors
              }
            }
          }
          // Retry up to 2 times for network issues
          return failureCount < 2;
        },
        retryDelay: (attemptIndex: number) => Math.min(1000 * Math.pow(2, attemptIndex), 5000),
        // Graceful error handling - don't fail completely
        throwOnError: false,
      }
    ),
    enabled: shouldFetch,
  });

  // Process the data to return a simple set of unavailable dates for quick lookups
  const responseData = query.data as AvailabilityResponse | undefined;
  const blockedDates = responseData?.availability?.blocked_dates || [];
  const unavailableDates = blockedDates.map(blocked => blocked.date);
  const unavailableDatesSet = new Set(unavailableDates);

  // Enhanced error state - distinguish between critical errors and minor issues
  const hasData = !!responseData;
  const hasCriticalError = query.isError && !hasData;
  const hasMinorError = query.isError && hasData; // Error but we have some data

  return {
    unavailableDates,
    unavailableDatesSet,
    isLoading: query.isLoading,
    isError: hasCriticalError, // Only report critical errors that prevent basic functionality
    hasMinorError, // Expose minor errors for optional warning messages
    error: query.error,
    lastUpdated: new Date().toISOString(), // We don't have this from the API, so generate it
    availabilityData: responseData?.availability, // Expose full availability data if needed
    refetch: query.refetch,
    hasData, // Indicate if we have any data at all
    isDateCovered: (date: string) => hasData && date >= startDate && date <= endDate,
    // Helper function to check if a specific date is unavailable
    isDateUnavailable: (date: string) => unavailableDatesSet.has(date),
  };
}

/**
 * Hook to fetch unavailable dates for a wider date range (e.g., for calendar month view)
 * Automatically calculates a reasonable date range based on current date
 */
export function useUnavailableDatesForCalendar(
  centerDate: Date = new Date(),
  monthsToFetch: number = 6,
  options: UseUnavailableDatesOptions & { viewedMonth?: Date } = {}
) {
  // Calculate date range for calendar view
  const startDate = new Date(centerDate);
  startDate.setMonth(startDate.getMonth() - 1);
  startDate.setDate(1);

  const endDate = new Date(centerDate);
  endDate.setMonth(endDate.getMonth() + monthsToFetch);
  endDate.setDate(0); // Last day of the previous month

  // If the user has navigated the calendar beyond the base window, extend the
  // window so the viewed month always has real availability data (otherwise
  // far-out months would silently render as fully available).
  const { viewedMonth, propertySlug, ...restOptions } = options;
  if (viewedMonth instanceof Date && !isNaN(viewedMonth.getTime())) {
    const viewedEnd = new Date(viewedMonth.getFullYear(), viewedMonth.getMonth() + 3, 0);
    if (viewedEnd > endDate) {
      endDate.setTime(viewedEnd.getTime());
    }
  }

  // Format as local dates — toISOString() shifts the day in negative-UTC
  // timezones (e.g. a guest browsing from Hawaii), skewing the fetched window.
  const toLocalDateStr = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const startDateStr = toLocalDateStr(startDate);
  const endDateStr = toLocalDateStr(endDate);

  return useUnavailableDates(startDateStr, endDateStr, {
    ...restOptions,
    propertySlug,
    // Calendar view can refresh less frequently since user is browsing
    refetchInterval: options.refetchInterval || 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to check availability for a specific date range with real-time updates
 * Useful for booking flows where we need immediate feedback
 */
export function useRealTimeAvailability(
  startDate: string,
  endDate: string,
  options: UseUnavailableDatesOptions = {}
) {
  return useUnavailableDates(startDate, endDate, {
    ...options,
    // More frequent updates for active booking process
    refetchInterval: options.refetchInterval || 1000 * 30, // 30 seconds
  });
}